import { useCallback, useEffect, useState } from 'react';
import { Badge, Skeleton } from '../components/primitives';
import type { AuditEntry } from '../types';
import { useAdmin } from './context';

const ACTION_META: Record<AuditEntry['action'], { label: string; tone: 'ok' | 'warn' | 'muted' | 'danger' | 'brand' }> = {
  'form.created': { label: 'Created form', tone: 'ok' },
  'form.updated': { label: 'Updated form', tone: 'brand' },
  'form.archived': { label: 'Archived form', tone: 'warn' },
  'section.created': { label: 'Created section', tone: 'ok' },
  'section.updated': { label: 'Updated section', tone: 'brand' },
  'section.deleted': { label: 'Deleted section', tone: 'danger' },
  'user.first-signin': { label: 'First sign-in', tone: 'muted' },
  'user.added': { label: 'Added user', tone: 'ok' },
  'user.role-changed': { label: 'Changed role', tone: 'brand' },
  'user.removed': { label: 'Removed user', tone: 'danger' },
};

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted">{label}</p>
      <pre className="max-h-64 overflow-auto rounded-lg bg-input p-3 text-[11px] leading-relaxed">
        {value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

export function AuditLog() {
  const { api } = useAdmin();
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [entityFilter, setEntityFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(
    async (cursor?: string) => {
      try {
        const res = await api.listAudit({
          limit: 50,
          ...(cursor ? { cursor } : {}),
          ...(entityFilter.trim() ? { entity: entityFilter.trim() } : {}),
          ...(actorFilter.trim() ? { actor: actorFilter.trim() } : {}),
        });
        setEntries((prev) => (cursor && prev ? [...prev, ...res.entries] : res.entries));
        setNextCursor(res.nextCursor);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load the audit log');
      }
    },
    [api, entityFilter, actorFilter],
  );

  useEffect(() => {
    setEntries(null);
    void load();
  }, [load]);

  return (
    <div>
      <h1 className="text-2xl font-bold">Audit log</h1>
      <p className="mt-1 text-sm text-muted">
        Every change in the Forms Hub — who did what, with before/after snapshots. Newest first.
      </p>

      <div className="mt-5 flex flex-wrap gap-3">
        <input
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value)}
          placeholder="Filter by form (e.g. hr/new-hire)…"
          aria-label="Filter by entity"
          className="w-full rounded-xl border border-border bg-input px-3.5 py-2 text-sm outline-none focus:border-brand sm:w-64"
        />
        <input
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value)}
          placeholder="Filter by person (email)…"
          aria-label="Filter by actor"
          className="w-full rounded-xl border border-border bg-input px-3.5 py-2 text-sm outline-none focus:border-brand sm:w-64"
        />
      </div>

      {error && (
        <div role="alert" className="mt-4 rounded-xl border border-danger/40 bg-danger/10 px-4 py-2.5 text-sm">
          {error}{' '}
          <button type="button" onClick={() => void load()} className="font-medium underline underline-offset-2">
            Retry
          </button>
        </div>
      )}

      {!entries && !error && (
        <div className="mt-6 flex flex-col gap-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {entries && entries.length === 0 && (
        <p className="mt-6 rounded-2xl border border-border bg-card px-6 py-10 text-center text-sm text-muted">
          No audit entries match.
        </p>
      )}

      {entries && entries.length > 0 && (
        <ul className="mt-6 flex flex-col gap-3">
          {entries.map((entry) => {
            const meta = ACTION_META[entry.action];
            return (
              <li key={entry.sk} className="rounded-2xl border border-border bg-card">
                <details className="group">
                  <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-3 gap-y-2 px-5 py-4 [&::-webkit-details-marker]:hidden">
                    <span className="text-xs tabular-nums text-muted">{formatTime(entry.at)}</span>
                    <Badge tone={meta.tone}>{meta.label}</Badge>
                    <code className="rounded bg-input px-1.5 py-0.5 font-mono text-xs">{entry.entity}</code>
                    <span className="text-xs text-muted">by {entry.actor}</span>
                    <span className="ml-auto text-muted transition-transform group-open:rotate-90" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </span>
                  </summary>
                  <div className="grid gap-4 border-t border-border px-5 py-4 sm:grid-cols-2">
                    <JsonBlock label="Before" value={entry.before} />
                    <JsonBlock label="After" value={entry.after} />
                  </div>
                </details>
              </li>
            );
          })}
        </ul>
      )}

      {nextCursor && (
        <div className="mt-6 text-center">
          <button
            type="button"
            disabled={loadingMore}
            onClick={() => {
              setLoadingMore(true);
              void load(nextCursor).finally(() => setLoadingMore(false));
            }}
            className="rounded-full border border-border px-5 py-2 text-sm font-medium transition-colors hover:bg-input disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load older entries'}
          </button>
        </div>
      )}
    </div>
  );
}
